// Der Montagetermin, mit dem Kunden vereinbart (E3, S7; Gernots
// Entscheidung 4: „ganz einfach … auch wenn er zwischendurch die App
// wechselt“): ein Tag und ein kurzer Text („Partie 2, 3 Tage“), „Speichern“.
// Kein Kalender, keine Partie-Planung – das ist die Kiste Termin.
//
// Steht auf „Ist im Büro!“ statt des Platzhalters „Gleich den Termin
// ausmachen“ (GesendetSeite.tsx, dunkel) und in der Einsicht einer
// gesendeten Besichtigung (Uebersicht.tsx) – derselbe Baustein, eintragen
// und ändern geht also auch später.
//
// Jede Änderung liegt sofort am Gerät (der Entwurf, ../gespraech.ts): Manuel
// schaut beim Kunden in seinen Kalender, und am iPhone kann eine App im
// Hintergrund beendet werden. Kommt er zurück, steht da, was er getippt hat
// – „noch nicht gespeichert“, bis er speichert. Danach: „Termin gespeichert –
// das Büro sieht ihn“ (ohne Empfang: „geht ans Büro, sobald Empfang da ist“).
// Seit dem Augentest E3b (23.10.2026): „Nicht speichern“ wirft den Entwurf
// weg, ein ✕ im Feld leert den Tag.

import { useState } from "react";
import { BUERO_GRENZEN } from "@happy-energy/kern";
import { Ikon, toastZeigen } from "@happy-energy/oberflaeche";
import type { Aufnahme, TerminEntwurf } from "../speicher.ts";
import { montageterminSpeichern, terminAmGeraet, terminEntwurfSichern, type TerminAmGeraet } from "../gespraech.ts";
import { speichernFehlgeschlagen, useSpeichern } from "./gemeinsam.tsx";

/** Ein Tag „JJJJ-MM-TT“ für Menschen: „Di., 4. Nov.“ – gerechnet im Kalender des Geräts, ohne Zeitzone. */
export function tagZeigen(am: string | null): string {
  if (!am) return "";
  const [jahr, monat, tag] = am.split("-").map(Number) as [number, number, number];
  const d = new Date(jahr, monat - 1, tag);
  if (Number.isNaN(d.getTime())) return am;
  const diesesJahr = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("de-AT", { weekday: "short", day: "numeric", month: "short", ...(diesesJahr ? {} : { year: "numeric" }) });
}

/** Der Termin in einer Zeile: „Di., 4. Nov. · Partie 2, 3 Tage“ – leer, wenn keiner. */
export function terminZeile(t: Pick<TerminAmGeraet, "am" | "text">): string {
  return [tagZeigen(t.am), t.text ?? ""].filter(Boolean).join(" · ");
}

/**
 * Der Baustein „Montagetermin“. Baut sich neu auf, wenn sich der
 * gespeicherte Termin ändert (gespeichert, vom Büro geändert) – ein Entwurf
 * am Gerät gilt dabei weiter.
 */
export function MontageterminKarte({ aufnahme, dunkel = false }: { aufnahme: Aufnahme; dunkel?: boolean }) {
  const termin = terminAmGeraet(aufnahme);
  return <TerminFeld key={`${termin.am ?? ""}|${termin.text ?? ""}`} aufnahme={aufnahme} termin={termin} dunkel={dunkel} />;
}

function TerminFeld({ aufnahme, termin, dunkel }: { aufnahme: Aufnahme; termin: TerminAmGeraet; dunkel: boolean }) {
  const [am, setAm] = useState(() => aufnahme.terminEntwurf?.am ?? termin.am ?? "");
  const [text, setText] = useState(() => aufnahme.terminEntwurf?.text ?? termin.text ?? "");
  const [arbeitet, setArbeitet] = useState(false);
  // Sofort am Gerät – kurz entprellt beim Tippen, beim Verlassen des Feldes und beim Wechsel in eine andere App gleich.
  const entwurf = useSpeichern((e: TerminEntwurf | null) => terminEntwurfSichern(aufnahme.id, e), 300);
  const wieGespeichert = (e: TerminEntwurf) => (e.am.trim() || null) === termin.am && (e.text.trim() || null) === termin.text;
  const geaendert = !wieGespeichert({ am, text });
  const id = `termin-${aufnahme.id}`;

  function aendern(neu: TerminEntwurf) {
    setAm(neu.am);
    setText(neu.text);
    // Wieder wie gespeichert: kein Entwurf – sonst stünde er da, wenn das Büro den Termin später ändert.
    entwurf.spaeter(wieGespeichert(neu) ? null : neu);
  }

  /**
   * „Nicht speichern“: zurück auf das zuletzt Gespeicherte, der Entwurf am
   * Gerät ist weg, nichts geht hinaus. Tippt man ins Datumsfeld, setzt das
   * iPhone gleich den heutigen Tag – ohne diesen Knopf blieb er als „Noch
   * nicht gespeichert“ stehen (Gernot, Augentest E3b, 23.10.2026).
   */
  async function nichtSpeichern() {
    entwurf.verwerfen();
    setAm(termin.am ?? "");
    setText(termin.text ?? "");
    try {
      await terminEntwurfSichern(aufnahme.id, null);
    } catch (fehler) {
      speichernFehlgeschlagen(fehler);
    }
  }

  async function speichern() {
    // Was noch als Entwurf unterwegs wäre, ist gleich gespeichert – nicht hinterher wieder Entwurf.
    entwurf.verwerfen();
    setArbeitet(true);
    try {
      const ergebnis = await montageterminSpeichern(aufnahme.id, { am, text });
      if (ergebnis === "ungueltig") toastZeigen("Dieses Datum gibt es nicht – bitte noch einmal wählen.", "fehler");
      else if (ergebnis === "gesperrt") toastZeigen("Den Termin gibt es erst, wenn die Besichtigung gesendet ist.", "fehler");
    } catch (fehler) {
      speichernFehlgeschlagen(fehler);
    } finally {
      setArbeitet(false);
    }
  }

  const gespeichert = termin.am !== null || termin.text !== null;
  return (
    <section id="montagetermin" className={`termin-karte${dunkel ? " dunkel" : ""}`} aria-labelledby={`${id}-titel`}>
      <div className="termin-kopf">
        <Ikon name="kalender" />
        <b id={`${id}-titel`}>Montagetermin, mit dem Kunden vereinbart</b>
      </div>
      {/* Untereinander, je über die ganze Breite: Nebeneinander ragte das Datumsfeld am iPhone ins Feld daneben
          (Gernots Augentest, 21.10.2026). Leer zeigt das iPhone im Datumsfeld nichts – dort steht dann „Tag wählen“. */}
      <div className="termin-felder">
        <div className="feld termin-feld">
          <label htmlFor={`${id}-am`}>Tag</label>
          <div className="termin-datum">
            <input id={`${id}-am`} type="date" value={am} onChange={(e) => aendern({ am: e.target.value, text })} onBlur={entwurf.sofort} />
            {!am && (
              <span className="termin-datum-leer" aria-hidden="true">
                Tag wählen
              </span>
            )}
            {/* „Zurücksetzen“ im Kalender des iPhones leert das Feld nicht – deshalb ein eigenes ✕ (Augentest E3b). */}
            {am && (
              <button type="button" className="termin-datum-weg" aria-label="Tag leeren" title="Tag leeren" onClick={() => aendern({ am: "", text })}>
                <Ikon name="kreuz" groesse="k" />
              </button>
            )}
          </div>
        </div>
        <div className="feld termin-feld">
          <label htmlFor={`${id}-text`}>Was dazu</label>
          <input
            id={`${id}-text`}
            type="text"
            maxLength={BUERO_GRENZEN.montageterminText}
            autoComplete="off"
            enterKeyHint="done"
            placeholder="z. B. Partie 2, 3 Tage"
            value={text}
            onChange={(e) => aendern({ am, text: e.target.value })}
            onBlur={entwurf.sofort}
          />
        </div>
      </div>
      {geaendert ? (
        <>
          <p className="termin-hinweis">Noch nicht gespeichert – das bleibt hier stehen, auch wenn du zwischendurch in den Kalender schaust.</p>
          <button type="button" className="knopf-haupt knopf-block" disabled={arbeitet} onClick={() => void speichern()}>
            <Ikon name="haken" /> Speichern
          </button>
          <button type="button" className={`knopf-rand knopf-block termin-verwerfen${dunkel ? " knopf-hell" : ""}`} disabled={arbeitet} onClick={() => void nichtSpeichern()}>
            Nicht speichern
          </button>
        </>
      ) : gespeichert ? (
        <p className="termin-gespeichert" role="status">
          <Ikon name="haken" groesse="k" />
          {termin.wartet ? "Termin gespeichert – geht ans Büro, sobald Empfang da ist" : "Termin gespeichert – das Büro sieht ihn"}
        </p>
      ) : (
        <p className="termin-hinweis">Noch keiner. Tag und kurz, was dazugehört – das Büro sieht ihn in der Akte und auf der Unterlage für die Montage.</p>
      )}
    </section>
  );
}
